import { useEffect, useState, useMemo, useRef } from "react";
import { Toast } from "src/components";
import MatrixChat from "src/scenes/Chat/models/MatrixChat";
import {
    CALL_ANSWER,
    CALL_HANGUP,
    CALL_INVITE,
    CALL_NOTE,
    CALL_REJECT,
    ROOM_ENCRYPTED,
    ROOM_MESSAGE
} from "src/constants/matrixEventTypes";

const CALL_EVENT_TYPES = [
    CALL_INVITE,
    CALL_ANSWER,
    CALL_HANGUP,
    CALL_NOTE,
    CALL_REJECT
];
const RENDERABLE_TIMELINE_EVENT_TYPES = [
    ROOM_MESSAGE,
    ROOM_ENCRYPTED,
    ...CALL_EVENT_TYPES
];

export const useChatMessage = (activeChatList, currentMatrixUserId) => {
    
    const [toggleDropdown, setToggleDropdown] = useState(false);
    const [profileModalIsShown, setProfileModalIsShown] = useState(false);
    const [profileModalShowsCurrentUser, setProfileModalShowsCurrentUser] = useState(false);
    const [profileModalUser, setProfileModalUser] = useState(null);
    const [profileModalUserLeadershipRoles, setProfileModalUserLeadershipRoles] = useState(null);
    const [encryptionComingSoonModalIsShown, setEncryptionComingSoonModalIsShown] = useState(false);

    const [openedMatrixChat, setOpenedMatrixChat] = useState(undefined);
    const [isFetchingRoomTimeline, setIsFetchingRoomTimeline] = useState(false);
    const [roomTimeline, setRoomTimeline] = useState([]);
    const [loadedRoomTimelineRoomId, setLoadedRoomTimelineRoomId] = useState(null);
    const [newMatrixEventUpdate, setNewMatrixEventUpdate] = useState(null);
    const timelineFetchRequestRef = useRef(0);

    useEffect(() => {
        if (!activeChatList) {
            return;
        }

        if (activeChatList.length === 0) {
            setOpenedMatrixChat(null);
            return;
        }
        
        if (openedMatrixChat === null || openedMatrixChat === undefined) {
            //auto-open index 0 of Active chat.
            const firstActiveChat = activeChatList[0];
            console.warn("No chat opened, auto-selecting the first one...", firstActiveChat);
            setOpenedMatrixChat(firstActiveChat);
            return;
        }

        const currentOpenedChat = activeChatList.find(
            matrixChat => matrixChat.matrixRoomId === openedMatrixChat.matrixRoomId
        );
        if (!currentOpenedChat) {
            //auto-open index 0 of Active chat triggered by onChange
            const firstActiveChat = activeChatList[0];
            setOpenedMatrixChat(firstActiveChat);
            return;
        }

        if (currentOpenedChat !== openedMatrixChat) {
            setOpenedMatrixChat(currentOpenedChat);
        }
        
        //do nothing
    }, [activeChatList]);

    const matrixRoomId = useMemo(() => openedMatrixChat?.matrixRoomId, [openedMatrixChat]);

    useEffect(() => {
        if (!canFetchTimeline(openedMatrixChat)) {
            timelineFetchRequestRef.current += 1;
            setRoomTimeline([]);
            setLoadedRoomTimelineRoomId(null);
            setIsFetchingRoomTimeline(false);
            return;
        }

        const seededTimeline = seedRoomTimelineFromLiveRoom(openedMatrixChat);
        refreshRoomTimeline(openedMatrixChat, !seededTimeline);
    }, [openedMatrixChat]);

    useEffect(() => {
        if (newMatrixEventUpdate === null) {
            return;
        }

        const newMatrixEvent = newMatrixEventUpdate.event;
        if (canFetchTimeline(openedMatrixChat) && openedMatrixChat.matrixRoomId === newMatrixEvent.getRoomId()) {
            if (CALL_EVENT_TYPES.includes(newMatrixEvent.getType())) {
                upsertTimelineEvent(newMatrixEvent);
                setNewMatrixEventUpdate(null);
                return;
            }

            refreshRoomTimeline(openedMatrixChat);
            setNewMatrixEventUpdate(null);
            return;
        }

        console.warn("Opened Room and MatrixEvent Room doesn't match", openedMatrixChat?.matrixRoomId, newMatrixEvent.getRoomId());
    }, [newMatrixEventUpdate]);

    const updateOpenedMatrixChatWithFormData = (apiResponseChat) => {
        if (matrixRoomId !== apiResponseChat.matrixRoomId) {
            return;
        }
        openedMatrixChat.updateFromApiResponseData(apiResponseChat);
        setOpenedMatrixChat(openedMatrixChat);
    };

    const upsertRoomTimeline = (matrixEvent) => {
        setNewMatrixEventUpdate({
            event: matrixEvent,
            updatedAt: Date.now(),
        });
    };

    const upsertTimelineEvent = (matrixEvent) => {
        setRoomTimeline(previousTimeline => {
            const timelineMap = new Map(previousTimeline.map(event => [event.getId(), event]));
            timelineMap.set(matrixEvent.getId(), matrixEvent);
            return Array.from(timelineMap.values())
                .sort((firstEvent, secondEvent) => firstEvent.getTs() - secondEvent.getTs());
        });
    };

    const canFetchTimeline = (matrixChat) => {
        const isMatrixChat = matrixChat instanceof MatrixChat ||
            typeof matrixChat?.fetchMatrixRoomTimeline === "function";
        return isMatrixChat && Boolean(matrixChat?.matrixClient?.getRoom);
    };

    const seedRoomTimelineFromLiveRoom = (matrixChat) => {
        const roomId = matrixChat?.matrixRoomId;
        const liveTimelineEvents = matrixChat?.matrixClient
            ?.getRoom?.(roomId)
            ?.getUnfilteredTimelineSet?.()
            ?.getLiveTimeline?.()
            ?.getEvents?.()
            ?.filter(event => RENDERABLE_TIMELINE_EVENT_TYPES.includes(event.getType())) || [];

        if (!roomId || !liveTimelineEvents.length) {
            return false;
        }

        setRoomTimeline(previousTimeline => {
            const timelineMap = new Map(
                [...previousTimeline, ...liveTimelineEvents]
                    .filter(event => event.getRoomId() === roomId)
                    .map(event => [event.getId(), event])
            );

            return Array.from(timelineMap.values())
                .sort((firstEvent, secondEvent) => firstEvent.getTs() - secondEvent.getTs());
        });
        setLoadedRoomTimelineRoomId(roomId);
        setIsFetchingRoomTimeline(false);
        return true;
    };

    const refreshRoomTimeline = async (matrixChat, showInitialLoading) => {
        const requestId = timelineFetchRequestRef.current + 1;
        timelineFetchRequestRef.current = requestId;
        const roomId = matrixChat.matrixRoomId;
        const isInitialRoomLoad = showInitialLoading && loadedRoomTimelineRoomId !== roomId;

        setIsFetchingRoomTimeline(isInitialRoomLoad);
        if (isInitialRoomLoad) {
            setRoomTimeline([]);
        }

        try {
            const events = await matrixChat.fetchMatrixRoomTimeline();
            if (timelineFetchRequestRef.current !== requestId) return;
            setRoomTimeline(previousTimeline => {
                const otherRoomEvents = previousTimeline
                    .filter(event => event.getRoomId() !== roomId);
                const currentRoomEvents = previousTimeline
                    .filter(event => event.getRoomId() === roomId);
                const timelineMap = new Map(
                    [...currentRoomEvents, ...events]
                        .map(event => [event.getId(), event])
                );

                return [
                    ...otherRoomEvents,
                    ...Array.from(timelineMap.values())
                        .sort((firstEvent, secondEvent) => firstEvent.getTs() - secondEvent.getTs())
                ];
            });
            setLoadedRoomTimelineRoomId(roomId);
        } catch (error) {
            if (timelineFetchRequestRef.current !== requestId) return;
            console.error(error);
            Toast.error("Fetching messages encountered an error.");
            setLoadedRoomTimelineRoomId(roomId);
        } finally {
            if (timelineFetchRequestRef.current === requestId && isInitialRoomLoad) {
                setIsFetchingRoomTimeline(false);
            }
        }
    };

    const displayOtherUserProfileModal = (userToShow = null) => {
        if (userToShow) {
            const isCurrentUser = userToShow.matrixUserId === currentMatrixUserId;
            const details = userToShow.employeeDetails || userToShow;
            displayProfileModal(details, userToShow.leadershipRoles, isCurrentUser);
            return;
        }
        const otherChatUser = openedMatrixChat.users.find(user => user.matrixUserId !== currentMatrixUserId);
        displayProfileModal(otherChatUser.employeeDetails, otherChatUser.leadershipRoles, false);
    };

    /**
     * @param {object} userToShow
     * @param {array} userLeadershipRoles
     * @param {boolean} isCurrentUser
     */
    const displayProfileModal = (userToShow, userLeadershipRoles, isCurrentUser) => {
        setProfileModalUser(userToShow);
        setProfileModalUserLeadershipRoles(userLeadershipRoles);
        setProfileModalIsShown(true);
        setProfileModalShowsCurrentUser(isCurrentUser);
    };

    const closeProfileModal = () => {
        setProfileModalIsShown(false);
    };

    return {
        openedMatrixChat,
        setOpenedMatrixChat,
        updateOpenedMatrixChatWithFormData,
        upsertRoomTimeline,
        isFetchingRoomTimeline, 
        roomTimeline, 
        loadedRoomTimelineRoomId,
        toggleDropdown,
        setToggleDropdown,
        displayProfileModal,
        displayOtherUserProfileModal,
        closeProfileModal,
        profileModalIsShown,
        profileModalShowsCurrentUser,
        profileModalUser,
        profileModalUserLeadershipRoles,
        encryptionComingSoonModalIsShown,
        setEncryptionComingSoonModalIsShown
    };
};
